import React, { useEffect, useState } from "react";
import SoundPlayer from "../../components/vertical/SoundPlayer/SoundPlayer";
import { WorkoutScreenData } from "../../types/screen.type";
import { ProgressBar } from "../../components/vertical/ProgressBar";
import { Videos } from "../../components/vertical/Video";
import { Header } from "../../components/vertical/Header";
import { Hydrate } from "../../components/vertical/Hydrate";
import { ChangeStation } from "../../components/vertical/ChangeStation";
import { Loading, ProgressCircle, Stay, PersonalTrainer } from "../../components";
import { Notification2 } from "../../components/vertical/Notification";
import { TimerControl } from "../../types/timer.type";
import { Finish } from "../../components/vertical/Finish";
import { useTranslation } from "react-i18next";
import "./Exercise.css";

interface ExerciseProps {
  screenData?: WorkoutScreenData;
  timerControlRef?: React.RefObject<TimerControl>;
  onLoad?: () => void;
}

export const Exercise: React.FC<ExerciseProps> = ({ screenData, timerControlRef, onLoad }): React.JSX.Element => {
  const [timeLeft, setTimeLeft] = useState<number | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    if (screenData && screenData.type !== "work" && screenData.progressCircle && screenData.progressCircle.initialTotalTime !== undefined) {
      setTimeLeft(screenData.progressCircle.initialTotalTime); // Initialize with the total time
    }
  }, [screenData, screenData?.progressCircle?.initialTotalTime]);

  useEffect(() => {
    if (timeLeft !== null && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prevTime) => (prevTime !== null ? prevTime - 1 : null));

      }, 1000); // Decrease the time every second

      return () => clearInterval(timer); // Cleanup timer on component unmount
    }
  }, [timeLeft]);

  useEffect(() => {
    if (onLoad) {
      onLoad(); // Notify that this component has finished loading
    }
  }, [onLoad]);

  if (!screenData) {
    return (
      <div className="workout">
        <div className="container-wrapper">
          <div className="container">
            <Loading message="Loading..." />
          </div>
        </div>
      </div>
    );
  } else {
    const halfTaskTime = screenData.progressCircle?.initialTotalTime ? screenData.progressCircle?.initialTotalTime / 2 : 1;
    const showVideos = (screenData.type !== "rest" && screenData.type !== "hydrate") ? true : timeLeft && timeLeft <= halfTaskTime;
    const showStayOrChangeStation = screenData.type === "rest" && timeLeft && timeLeft > halfTaskTime;
    const showHydrate = screenData.type === "hydrate" && timeLeft && timeLeft > halfTaskTime;

    return (
      <div className="workout">
        <div className="container-wrapper">
          <div className="container">
            {screenData.header?.show && (
              <Header
                icon={screenData.header.icon}
                label={t('next_class')}
                time={screenData.header.time} // Use formatted workout time
              />
            )}

            {screenData.progressCircle?.show && (
              <ProgressCircle
                ref={timerControlRef}
                initialTotalTime={screenData.progressCircle.initialTotalTime}
                color={screenData.progressCircle.color}
                bypassCacheGuid={screenData.progressCircle.bypassCacheGuid}
              />
            )}

            {screenData.progressBar?.show && (
              <ProgressBar
                total={screenData.progressBar.numberOfElements}
                progress={screenData.progressBar.progress}
                label={screenData.progressBar.label}
                time={screenData.progressBar.time}
                hideOnTimeLeft={screenData.progressBar.hideOnTimeLeft}
                bypassCacheGuid={Date.now().toString()}
              />
            )}

            {showStayOrChangeStation && screenData.stay && screenData.sets && (
              <Stay message={screenData.stay?.message}
                total={screenData.sets.total}
                active={screenData.sets.active} />
            )}

            {showStayOrChangeStation && screenData.changeStation && (
              <ChangeStation message={screenData.changeStation?.message} />
            )}

            {showHydrate && screenData.batteryMedium && <Hydrate message={screenData.batteryMedium?.message} />}

            <Videos
              hidden={!showVideos}
              taskType={screenData.type}
              key={screenData.type == 'warmup' ? 'warmup' : 'work'}
              exerciseStationVideos={screenData.videos?.exercisesStation}
              showStations={screenData.videos?.showStations}
              isPersonalTrainerLayout={screenData.personalTrainer ? true : false}
              isGroupRoutine={screenData.videos?.isGroupRoutine} />

            {screenData.notification?.show && (
              <Notification2
                message={screenData.notification.message}
                time={screenData.notification.time}
                showOnTimeLeft={screenData.notification.showOnTimeLeft}
                bypassCacheGuid={Date.now().toString()}
              />
            )}

            {screenData.finish?.show && (
              <Finish message={screenData.finish.message} img={screenData.finish?.icon} />
            )}

            {screenData.personalTrainer?.show && (
              <PersonalTrainer
                name={screenData.personalTrainer.name}
                jobTitle={screenData.personalTrainer.job_title}
                photo={screenData.personalTrainer.photo}
              />
            )}

            <SoundPlayer
              soundFile="/sound/notification_alert_ping_bright.mp3"
              play={false}
            />
          </div>
        </div>
      </div>
    );
  }
};
